import type React from "react";
import type { Charity } from "../types";

interface SpecificAllocationSectionProps {
	charities: Charity[];
	allocations: Record<string, number>;
	onAllocationChange: (charityId: string, amount: number) => void;
	isVisible: boolean;
}

export const SpecificAllocationSection: React.FC<
	SpecificAllocationSectionProps
> = ({ charities, allocations, onAllocationChange, isVisible }) => {
	if (!isVisible) return null;

	// Group charities by category
	const categories = {
		"Charity funds": [] as Charity[],
		"Top recommended charities": [] as Charity[],
		"Other effective charities we support": [] as Charity[],
	};

	charities.forEach((charity) => {
		if (charity.category === "Charity funds") {
			categories["Charity funds"].push(charity);
		} else if (charity.category === "Top recommended charities") {
			categories["Top recommended charities"].push(charity);
		} else if (charity.category === "Other effective charities") {
			categories["Other effective charities we support"].push(charity);
		}
	});

	const handleAllocationChange = (charityId: string, value: string) => {
		const amount = parseFloat(value) || 0;
		onAllocationChange(charityId, amount);
	};

	const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
		e.target.classList.add("validate");
	};

	const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
		if (e.target.value === "") {
			e.target.classList.remove("validate");
		}
	};

	return (
		<section
			id="specific-allocation-section"
			className="specific-allocation-section"
		>
			{Object.entries(categories).map(([categoryName, categoryCharities]) => (
				<div key={categoryName}>
					<h3>{categoryName}</h3>

					{categoryCharities.map((charity) => {
						// Italicize fund names
						const charityName =
							charity.category === "Charity funds" ? (
								<i>{charity.name}</i>
							) : (
								<span>{charity.name}</span>
							);

						return (
							<div key={charity.id} className="flex charity-option">
								<label htmlFor={`charity-${charity.id}`}>{charityName}</label>
								<span className="flex items-center rounded-sm p-2 text-md border focus-outline">
									<input
										type="number"
										inputMode="numeric"
										min="0"
										max="999999.99"
										step="0.01"
										id={`charity-${charity.id}-amount`}
										name={`charity-${charity.id}-amount`}
										className="specific-charity inline-block text-md"
										value={allocations[charity.id.toString()] || ""}
										onChange={(e) =>
											handleAllocationChange(
												charity.id.toString(),
												e.target.value,
											)
										}
										onFocus={handleFocus}
										onBlur={handleBlur}
										style={{ border: "none", outline: "none", width: "100%" }}
									/>
									<span className="currency-suffix">HKD</span>
								</span>
							</div>
						);
					})}
				</div>
			))}
		</section>
	);
};
